"use client";

import { EMPTY_AXES, isAxesEmpty } from "@/lib/mobility/bike-facets";
import { hubEmptyCause } from "@/lib/mobility/station-hub";
import { EmptyStateLayout } from "../EmptyStateLayout";
import { hubKey, useHub, useHubDensity } from "./hub-context";

/** 빈 화면 그림 — 홈 카테고리 타일과 같은 3D 자산(캠핑 = 주황 핀, 바이크 = 오토바이). */
const EMPTY_ILLUSTRATION = {
  camping: "/icons/categories/home-nearby-3d-v2-320.webp",
  mobility: "/icons/categories/home-motorcycle-3d-v1-320.webp",
} as const;

/**
 * 지점 목록이 비었을 때(09-29 미리보기 확정안) — 그림 + 제목(상황) + 설명(다음 행동) + 버튼.
 *
 * 원인은 `hubEmptyCause` 가 고른다:
 *  - 필터 축 때문 → "조건에 맞는 대여 지점이 없어요" + [필터 초기화]
 *  - "이 지역에서 검색" 범위 때문 → "이 지역에는 아직 … 없어요" + [전체 … 보기](범위 해제)
 *  - 그 밖(데이터 없음) → 기존 "조건에 맞는 … 없습니다" 한 줄, 버튼 없음
 *
 * 모바일 드로어·태블릿은 가운데 세로 정렬, 데스크톱 목록 열(wide)은 그림 왼쪽 · 글 오른쪽.
 */
export function HubEmptyState() {
  const { kind, t, data, axes, setAxes, areaBounded, clearArea } = useHub();
  const wide = useHubDensity() === "wide";

  const cause = hubEmptyCause({
    // 축은 허브가 종류별로 걸러 둔 값이다(모빌리티 = 바이크 축, 캠핑 = 인원)
    filtersActive: !isAxesEmpty(axes),
    areaBounded,
    // data.stations = 범위만 적용한 지점(필터 축 적용 전)
    unfilteredCount: data.stations.length,
  });

  const copy =
    cause === "filters"
      ? {
          title: t(hubKey(kind, "emptyFilterTitle")),
          body: t(hubKey(kind, "emptyFilterBody")),
          action: t("hub.emptyFilterAction"),
          onAction: () => setAxes({ ...EMPTY_AXES }),
        }
      : cause === "area"
        ? {
            title: t(hubKey(kind, "emptyAreaTitle")),
            body: t(hubKey(kind, "emptyAreaBody")),
            action: t(hubKey(kind, "emptyAreaAction")),
            onAction: clearArea,
          }
        : {
            title: t(hubKey(kind, "noResult")),
            body: null,
            action: null,
            onAction: null,
          };

  return (
    <EmptyStateLayout
      image={EMPTY_ILLUSTRATION[kind]}
      title={copy.title}
      body={copy.body}
      wide={wide}
    >
      {copy.action && copy.onAction ? (
        <button
          type="button"
          onClick={copy.onAction}
          className="mt-[18px] h-10 rounded-full border border-gray-300 bg-white px-[18px] text-sm font-medium text-gray-900 transition hover:bg-gray-50"
        >
          {copy.action}
        </button>
      ) : null}
    </EmptyStateLayout>
  );
}
